// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControlLabel,
  Switch,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
  Typography,
  Paper
} from '@mui/material';
import { getCitationSettings, toggleCitationSettings } from '../../services/api'; // Adjust the import path as necessary

const CitationSettings = ({ clientId, onStatusChange, onSaveClick }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [citationsEnabled, setCitationsEnabled] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchSettings();
  }, [clientId]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const data = await getCitationSettings(clientId);
      
      if (data.success) {
        setCitationsEnabled(data.citations_enabled);
        // Notify parent component of current status
        if (onStatusChange) {
          onStatusChange(data.citations_enabled);
        }
      } else {
        setSnackbar({
          open: true,
          message: data.message || 'Failed to load citation settings',
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCitations = async (event) => {
    const newState = event.target.checked;
    
    try {
      // Set local state immediately for responsive UI
      setCitationsEnabled(newState);
      setSaving(true);
      
      // Call the onSaveClick callback to notify parent component
      if (onSaveClick) {
        onSaveClick();
      }
      
      console.log('[CitationSettings] Saving settings, setting enabled to:', newState);
      
      const data = await toggleCitationSettings(clientId, newState);
      
      if (data.success) {
        // Update the local state based on the response from the server
        const serverEnabledState = data.citations_enabled;
        console.log('[CitationSettings] Server returned citations_enabled:', serverEnabledState);
        
        // If server state doesn't match what we sent, update our local state to match server
        if (serverEnabledState !== newState) {
          console.warn('[CitationSettings] Server state differs from local state, updating local state');
          setCitationsEnabled(serverEnabledState);
        }
        
        setSnackbar({
          open: true,
          message: `Citations ${serverEnabledState ? 'enabled' : 'disabled'} successfully`,
          severity: 'success'
        });
        
        // Notify parent component of updated status from server
        if (onStatusChange) {
          onStatusChange(serverEnabledState);
        }
        
        // Dispatch a custom event to notify other components about the change
        window.dispatchEvent(new Event('citation-settings-changed'));
      } else {
        // Revert to original state if save failed
        setCitationsEnabled(!newState);
        setSnackbar({
          open: true,
          message: data.message || 'Failed to save citation settings',
          severity: 'error'
        });
      }
    } catch (error) {
      // Revert to original state if save failed
      setCitationsEnabled(!newState);
      console.error('Error saving citation settings:', error);
      setSnackbar({
        open: true,
        message: 'Unable to save citation settings',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Citation Settings
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Enable or disable citations in AI responses. When enabled, responses will include professionally formatted 
          citations with numbered references in text ([1], [2], etc.) and a clean Sources section at the end, 
          matching Claude's citation style.
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={citationsEnabled}
              onChange={handleToggleCitations}
              name="citations-enabled"
              color="primary"
              disabled={saving}
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography>Enable Citations</Typography>
              {saving && <CircularProgress size={16} sx={{ ml: 1 }} />}
            </Box>
          }
        />
        
        {citationsEnabled && (
          <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: 'background.paper' }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Citation Style
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Citations will appear inline in the text as numbered references [1], [2], etc., with a formatted 
              Sources section at the end of each response. All sources include titles and URLs when available.
            </Typography>
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Example:
              </Typography>
              <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 1, fontSize: '0.9rem' }}>
                "The latest advancements in machine learning involve transformer architectures [1], which have revolutionized 
                natural language processing. Recent studies show significant improvements in performance metrics [2] compared to 
                previous approaches."
                <Box component="div" sx={{ mt: 2, pt: 1, borderTop: '1px solid #e0e0e0', fontSize: '0.8rem' }}>
                  <strong>Sources</strong>
                  <ol style={{ marginTop: '4px', paddingLeft: '20px' }}>
                    <li>arxiv.org - Attention Is All You Need (https://arxiv.org/abs/1706.03762)</li>
                    <li>openai.com - Language Models are Few-Shot Learners (https://openai.com/research/language-models-are-few-shot-learners)</li>
                  </ol>
                </Box>
              </Box>
            </Box>
          </Paper>
        )}
      </Box>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

CitationSettings.defaultProps = {
  onStatusChange: () => {}, // Default no-op function if not provided
  onSaveClick: () => {} // Default no-op function if not provided
};

export default CitationSettings; 